import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createPromiseAction } from '@adobe/redux-saga-promise';
import { FETCH_PROFILE, GET_PROFILE } from 'actions/profile';
import MediaQuery from 'react-responsive';
import MainLayout from 'components/layout/index';
import { onMessageListener } from '../../firebase';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { MessagePopup } from 'App';
import { GET_DASHBOARD_DATA } from 'actions/dashboard';
import { FETCH_CHARGER_BY_STATION, GET_CHARGER } from 'actions/charger';
import { FETCH_NOTIFICATION } from 'actions/notification';
import moment from 'moment';
import { MANAGE_ROLE } from 'actions/role';
import { cookie } from 'utils/cookies/cookies';
import PropTypes from 'prop-types';
import { DEALER_PARTNER } from 'components/common/constant';

export const RealTimeData = createContext();
export const NavContext = createContext();

const parseQueryString = (queryString) => {
  queryString = queryString.replace(/^\?/, '');
  const pairs = queryString.split('&');
  const params = {};
  pairs.forEach((pair) => {
    const [key, value] = pair.split('=');
    params[key] = value;
  });
  return params;
};

// CDB-2017 — a route flagged `moduleGuard` opens only for roles holding its module, by the same
// rule as the sidebar (`isAllowed`). Anyone else reaching it by direct URL goes to the dashboard.
// Until the role's modules have loaded, `isAllowed` lets everything through, so nobody is sent
// away while the page is starting.
const ModuleGuardedRoute = ({ route }) => {
  const { isAllowed } = useContext(NavContext);
  const location = useLocation();
  if (!isAllowed(route.moduleKey)) {
    // Keep the query string: `?tenant=` carries an admin's tenant session.
    return <Navigate to={{ pathname: '/dashboard', search: location.search }} replace />;
  }
  return <route.component {...route} />;
};

ModuleGuardedRoute.propTypes = {
  route: PropTypes.object.isRequired,
};

const renderRoutes = (routes) => {
  return routes
    .map((route, index) => {
      if (!route || typeof route !== 'object') {
        console.error('Invalid route object:', route);
        return null;
      }
      if (!route.path || typeof route.path !== 'string') {
        console.error('Invalid route path:', route.path);
        return null;
      }
      if (!route.component || typeof route.component !== 'function') {
        console.error('Invalid route component:', route.component);
        return null;
      }
      const element = route.moduleGuard && route.moduleKey ? <ModuleGuardedRoute route={route} /> : <route.component {...route} />;
      return <Route key={`${route.path}-${index}`} path={route.path} element={element} />;
    })
    .filter(Boolean); // Remove any null elements
};

const PrivateRoute = ({ routes }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [userT, setUserT] = useState();
  const [realTimeMessageType, setRealTimeMessageType] = useState(false);
  const userData = useSelector((state) => state.profile.userProfile);
  const rolePermissionData = useSelector((state) => state.manageRole.manageRoles);
  const excludedModules = useSelector((state) => state.manageRole?.excludedModules);
  const navigateTo = (path, state) => {
    if (path) {
      const queryParams = parseQueryString(path);
      const searchParams = new URLSearchParams(location.search);
      let paramsLength = Object.keys(queryParams).length;
      let tenant = searchParams.get('tenant');
      let newPath = path;
      let lastKey;
      for (const key in queryParams) {
        lastKey = key;
      }
      if (tenant && paramsLength >= 1) {
        if (queryParams[lastKey]) {
          newPath = newPath + '&tenant=' + tenant;
        } else {
          newPath = newPath + '?tenant=' + tenant;
        }
      }
      if (tenant && paramsLength == 0 && queryParams[lastKey]) {
        newPath = newPath + '?tenant=' + tenant;
      }
      navigate(newPath, state);
    }
  };

  const isAllowed = useCallback(
    (key) => {
      const currentRole = userT?.role || userData?.role || cookie.get('user-role') || cookie.get('appUserRole');
      if (['super_admin', 'saas_admin', 'md_admin'].includes(currentRole)) return true;

      const excludedModulesids = (excludedModules || []).map((exMod) => exMod.id);
      const allowModulesKeys =
        rolePermissionData?.[0]?.modules?.filter((module) => !excludedModulesids.includes(module.id))?.map((module) => module.key) || [];

      if (!rolePermissionData || rolePermissionData.length === 0 || allowModulesKeys.length === 0) {
        return true;
      }

      return allowModulesKeys.includes(key);
    },
    [JSON.stringify(rolePermissionData), JSON.stringify(excludedModules), userT, userData]
  );

  const getProfile = useCallback((data) => {
    const fetchPostAction = createPromiseAction(FETCH_PROFILE.REQUEST);
    return dispatch(fetchPostAction(data));
  }, []);

  const setUser = useCallback((data) => dispatch({ type: GET_PROFILE.SUCCESS, payload: data }), []);

  const getDashboard = useCallback(() => {
    dispatch({ type: GET_DASHBOARD_DATA.REQUEST });
  }, []);

  const getChargers = useCallback((data = {}) => {
    dispatch({ type: FETCH_CHARGER_BY_STATION.REQUEST, payload: data });
  }, []);

  const getChargerDetail = useCallback((id) => {
    dispatch({ type: GET_CHARGER.REQUEST, payload: id });
  }, []);

  const getGeneralNotification = useCallback((data = {}) => {
    dispatch({ type: FETCH_NOTIFICATION.REQUEST, payload: data });
  }, []);

  useEffect(() => {
    if (userT?.role !== undefined && userT?.role !== '' && userT?.role !== DEALER_PARTNER) {
      dispatch({ type: MANAGE_ROLE.REQUEST, payload: { name: userT.role } });
    }
  }, [userT, dispatch]);

  const onMessageReceived = useCallback(
    (payload) => {
      setRealTimeMessageType(payload.data.type);
      if (payload.data.type === 'update_dashboard') {
        if (location.pathname === '/dashboard') {
          getDashboard();
        } else if (payload.data.for === 'charger' && location.pathname === `/stations/${get(payload, 'data.station')}`) {
          getChargers({ id: get(payload, 'data.station') });
        } else if (payload.data.for === 'charger' && location.pathname === `/charger/${get(payload, 'data.charger')}`) {
          getChargerDetail(get(payload, 'data.charger'));
        }
      } else {
        if (location.pathname === '/notification') {
          const allNotification = {
            from: moment(moment().startOf('month')).format('YYYY-MM-DD HH:mm:ss'),
            to: moment(moment(new Date()).endOf('day')).format('YYYY-MM-DD HH:mm:ss'),
          };
          getGeneralNotification(allNotification);
        }
        const title = get(payload, 'data.title', '');
        const description = get(payload, 'data.description', '');
        const options = {
          autoClose: 6000,
          type: toast.TYPE.INFO,
          position: toast.POSITION.TOP_RIGHT,
          pauseOnHover: true,
        };
        if (title) {
          toast(<MessagePopup title={title} description={description} />, options);
        }
      }
    },
    [location]
  );

  onMessageListener(onMessageReceived)
    .then()
    .catch((err) => console.error('Failed: ', err));

  const tenantId = new URLSearchParams(location.search).get('tenant');
  const tenantSessionToken = localStorage.getItem(`${tenantId}_token`);
  let token = (tenantId && tenantSessionToken && tenantSessionToken !== 'null' && tenantSessionToken !== 'undefined')
    ? tenantSessionToken
    : cookie.get('token');

  useEffect(() => {
    if (token) {
      getProfile()
        .then((res) => {
          const user2 = res.data;
          setUserT(user2);
          setUser(user2);
        })
        .catch((err) => {
          console.error('Profile fetch failed:', err);
          if (tenantId) {
            localStorage.removeItem(`${tenantId}_token`);
            const searchParams = new URLSearchParams(location.search);
            searchParams.delete('tenant');
            const cleanSearch = searchParams.toString();
            const cleanUrl = location.pathname + (cleanSearch ? `?${cleanSearch}` : '');
            window.location.href = cleanUrl;
          } else {
            cookie.clean();
            navigate('/login');
          }
        });
    } else if (!token || !userData) {
      navigate('/login');
    }
  }, []);

  return (
    <NavContext.Provider value={{ navigateTo, isAllowed }}>
      <RealTimeData.Provider value={realTimeMessageType}>
        <MediaQuery minWidth={800}>
          {(matches) => (
            <MainLayout showToggle={!matches}>
              {cookie.get('user-role') === DEALER_PARTNER ? (
                <Routes>
                  {renderRoutes(routes.filter((route) => route.path === '/partnerDashboard'))}
                  <Route path="*" element={<Navigate to="/partnerDashboard" replace />} />
                </Routes>
              ) : (
                <Routes>{renderRoutes(routes)}</Routes>
              )}
            </MainLayout>
          )}
        </MediaQuery>
      </RealTimeData.Provider>
    </NavContext.Provider>
  );
};

PrivateRoute.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      component: PropTypes.elementType.isRequired,
      permission: PropTypes.string,
      moduleKey: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string),
    })
  ).isRequired,
};

export default PrivateRoute;
